import type { DropzoneInputProps, DropzoneRootProps } from "react-dropzone";

import { MAX_SCREENSHOTS } from "../SubmitApp.utils";

interface GraphicsFieldsDropzoneProps {
  rootProps: DropzoneRootProps;
  inputProps: DropzoneInputProps;
}

export interface GraphicsFieldsPresentationProps {
  logo: string;
  screenshots: string[];
  screenshotError?: string;
  logoDropzone: GraphicsFieldsDropzoneProps;
  screenshotsDropzone: GraphicsFieldsDropzoneProps;
  onRemoveScreenshot: (index: number) => void;
}

const DEFAULT_LOGO_SRC = "resources/images/submit-app-connect/submit-logo.svg";

export const GraphicsFieldsPresentation = ({
  logo,
  screenshots,
  screenshotError,
  logoDropzone,
  screenshotsDropzone,
  onRemoveScreenshot,
}: GraphicsFieldsPresentationProps) => (
  <section className="flex flex-col gap-4">
    <h2 className="m-0 text-2xl">Graphics</h2>

    <div className="flex flex-col gap-2">
      <h3 className="m-0 text-xl">App Logo</h3>
      <p className="mb-0 text-neutral-600">
        Your app logo as shown on Alpaca Connect
      </p>
      <div className="flex flex-wrap items-center gap-8">
        <div
          {...logoDropzone.rootProps}
          className="cursor-pointer rounded-full outline-offset-4 focus:outline focus:outline-2 focus:outline-yellow-500"
        >
          <input {...logoDropzone.inputProps} />
          <img
            alt="App logo preview"
            className="size-32 rounded-full object-cover"
            src={logo || DEFAULT_LOGO_SRC}
          />
        </div>
        <p className="m-0 text-neutral-600">
          Extensions: png, jpg, gif
          <br />
          Dimensions max: 256px x 256px
          <br />
          Up to 1 MB
        </p>
      </div>
    </div>

    <div className="flex flex-col gap-2">
      <h3 className="m-0 text-xl">Screenshots</h3>
      <ol className="ml-5 flex list-decimal flex-col gap-1">
        <li>Please provide screenshots of all customer-facing pages.</li>
        <li>
          Please ensure to include a screenshot of the disclosure language below
          that should appear at the time of connection between OAuth and Alpaca.
        </li>
      </ol>
      <code className="inline-block rounded bg-gray-100 p-3">
        Authorize [Your App Name] By allowing [Your App Name] to access your
        Alpaca account, you are granting [Your App Name] access to your account
        information and authorization to place transactions in your account at
        your direction. Alpaca does not warrant or guarantee that [Your App
        Name] will work as advertised or expected. Before authorizing, learn
        more about [Your App Name].
      </code>

      <div className="mt-4 flex flex-wrap gap-6">
        {screenshots.map((screenshot, index) => (
          <div
            className="relative aspect-video w-48 overflow-hidden rounded-xl"
            key={screenshot}
          >
            <img
              alt={`Screenshot ${index + 1}`}
              className="h-full w-full object-cover"
              src={screenshot}
            />
            <button
              aria-label={`Remove screenshot ${index + 1}`}
              className="absolute top-2 right-2 rounded bg-black/70 px-2 py-1 text-white text-xs"
              onClick={() => onRemoveScreenshot(index)}
              type="button"
            >
              Remove
            </button>
          </div>
        ))}

        {screenshots.length < MAX_SCREENSHOTS && (
          <div
            {...screenshotsDropzone.rootProps}
            className="flex w-60 cursor-pointer items-center rounded-xl outline-offset-4 focus:outline focus:outline-2 focus:outline-yellow-500"
          >
            <input {...screenshotsDropzone.inputProps} />
            <img
              alt=""
              aria-hidden="true"
              className="aspect-video w-48 rounded-xl object-cover"
              src="resources/images/submit-app-connect/submit-screenshot.svg"
            />
            <img
              alt=""
              aria-hidden="true"
              className="ml-2"
              src="resources/images/submit-app-connect/submit-img-btn.svg"
            />
          </div>
        )}
      </div>
      {screenshotError && (
        <p className="mt-1 text-red-600 text-xs" role="alert">
          {screenshotError}
        </p>
      )}
      <p className="m-0 text-neutral-600">
        Extensions: png, jpg
        <br />
        Dimensions: 1024px x 500px
        <br />
        Aspect ratio: 16:9
        <br />
        Up to 2 MB
      </p>
    </div>
  </section>
);
